import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Input, Select, Spin, Empty, message, Button, Dropdown, Avatar, Space, FloatButton, Tooltip } from 'antd';
import { SearchOutlined, LoginOutlined, LogoutOutlined, UserOutlined, SettingOutlined, VideoCameraAddOutlined, TrophyOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import LeaderboardCard from '../components/LeaderboardCard';
import StudyProgramLeaderboardCard from '../components/StudyProgramLeaderboardCard';
import VideoSubmissionModal from '../components/VideoSubmissionModal';
import UploadPromotionBanner from '../components/UploadPromotionBanner';
import { leaderboardAPI, handleApiError, apiClient } from '../services/api';
import './Home.css';

const { TabPane } = Tabs;
const { Option } = Select;

// Transform backend API response to frontend format for users
const transformUserData = (data, role) => {
  return data.map((item) => ({
    affiliate_id: item.affiliate_id,  // Add affiliate_id for detail page navigation
    nim_nip: item.external_id,
    name: item.name,
    email: item.email || '',
    photo: '',  // Backend doesn't provide photo yet
    role: role,
    study_program: item.unit_name || '',
    total_videos: item.total_verifications || 0,
    total_certifications: item.total_certifications || 0,  // Add certifications
    total_points: item.total_score || 0,
    rank: item.rank,
    badges: []  // Can be calculated based on points
  }));
};

// Transform backend API response to frontend format for study programs
const transformStudyProgramData = (data) => {
  return data.map((item) => ({
    code: item.unit_code,
    name: item.unit_name,
    faculty: item.parent_unit_name || item.unit_type || 'program_studi',
    faculty_code: item.parent_unit_code,
    description: item.unit_description,
    type: item.unit_type,
    // Contributor distribution percentages
    total_dosen: item.total_dosen || 0,
    total_mahasiswa: item.total_mahasiswa || 0,
    total_tendik: item.total_tendik || 0,
    // Actual contributor counts
    dosen_count: item.dosen_count || 0,
    mahasiswa_count: item.mahasiswa_count || 0,
    tendik_count: item.tendik_count || 0,
    total_contributors: item.total_members || 0,
    total_videos: item.total_verifications || 0,
    total_points: item.total_score || 0,
    participation_percentage: item.participation_percentage || 0,
    rank: item.rank
  }));
};

const Home = () => {
  const navigate = useNavigate();
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [studyProgramsData, setStudyProgramsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState('points');
  const [activeTab, setActiveTab] = useState('study_programs');
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [user, setUser] = useState(null);
  const [videoSubmissionModalVisible, setVideoSubmissionModalVisible] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Easter egg states
  const [trophySpinning, setTrophySpinning] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [yearBadgeClicked, setYearBadgeClicked] = useState(false);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      if (token && storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (error) {
          console.error('Failed to parse user data:', error);
        }
      }
    };
    checkAuth();
  }, []);

  // Handle OAuth callback - extract token from URL if present
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    const emailFromUrl = urlParams.get('email');
    const nameFromUrl = urlParams.get('name');
    const isAdminFromUrl = urlParams.get('is_admin');

    if (tokenFromUrl) {
      // Store token and user info from OAuth callback
      localStorage.setItem('authToken', tokenFromUrl);
      localStorage.setItem('token', tokenFromUrl);  // Also store as 'token'
      const userData = {
        email: emailFromUrl,
        name: nameFromUrl,
        is_admin: isAdminFromUrl === 'true'
      };
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);

      // Clean URL by removing query parameters
      window.history.replaceState({}, document.title, window.location.pathname);

      message.success(`Selamat datang, ${nameFromUrl}!`);
    }
  }, []);

  // Load campaigns on component mount
  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        const response = await apiClient.get('/api/campaigns');
        if (response.data.status === 'success') {
          setAvailableCampaigns(response.data.data || []);
        }
      } catch (error) {
        console.error('Failed to load campaigns:', error);
        setAvailableCampaigns([]);
      }
    };

    loadCampaigns();
  }, []);

  // Load summary/certification stats on mount
  useEffect(() => {
    const loadSummary = async () => {
      setSummaryLoading(true);
      try {
        const data = await leaderboardAPI.getSummary();
        if (data.status === 'success') {
          setSummaryData(data.data);
        }
      } catch (error) {
        console.error('Failed to load summary:', error);
      } finally {
        setSummaryLoading(false);
      }
    };

    loadSummary();
  }, []);

  // Fetch data based on active tab, campaign filter, and search text
  const fetchDataForTab = useCallback(async (tab, campaign_id = null, search = null) => {
    setLoading(true);
    try {
      let data;

      switch (tab) {
        case 'study_programs':
          data = await leaderboardAPI.getStudyPrograms(100, campaign_id);
          if (data.status === 'success') {
            setStudyProgramsData(transformStudyProgramData(data.data));
          }
          break;
        case 'lecturers':
          data = await leaderboardAPI.getLecturers(100, campaign_id, search);
          if (data.status === 'success') {
            setLeaderboardData(transformUserData(data.data, 'lecturer'));
            setFilteredData(transformUserData(data.data, 'lecturer'));
          }
          break;
        case 'teaching_staff':
          data = await leaderboardAPI.getTeachingStaff(100, campaign_id, search);
          if (data.status === 'success') {
            setLeaderboardData(transformUserData(data.data, 'teaching_staff'));
            setFilteredData(transformUserData(data.data, 'teaching_staff'));
          }
          break;
        case 'students':
          data = await leaderboardAPI.getStudents(100, campaign_id, search);
          if (data.status === 'success') {
            setLeaderboardData(transformUserData(data.data, 'student'));
            setFilteredData(transformUserData(data.data, 'student'));
          }
          break;
        default:
          break;
      }
    } catch (error) {
      const apiError = handleApiError(error);
      console.error('Error fetching data:', apiError);
      message.error('Gagal memuat data: ' + apiError.message);
      // Fallback to empty arrays on error
      if (tab === 'study_programs') {
        setStudyProgramsData([]);
      } else {
        setLeaderboardData([]);
        setFilteredData([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch data when tab, campaign, or search changes
  useEffect(() => {
    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(() => {
      fetchDataForTab(activeTab, selectedCampaign, searchText);
    }, 300); // Wait 300ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [activeTab, selectedCampaign, searchText, fetchDataForTab]);

  // Filter study programs
  const getFilteredStudyPrograms = () => {
    if (activeTab !== 'study_programs') return [];

    return studyProgramsData.filter((prodi) => {
      if (!searchText) return true;

      const searchLower = searchText.toLowerCase();
      return (
        prodi.name.toLowerCase().includes(searchLower) ||
        prodi.code.toLowerCase().includes(searchLower) ||
        (prodi.faculty && prodi.faculty.toLowerCase().includes(searchLower))
      );
    });
  };

  // Sort study programs
  const getSortedStudyPrograms = () => {
    const filtered = getFilteredStudyPrograms();

    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'points':
          return b.total_points - a.total_points;
        case 'videos':
          return b.total_videos - a.total_videos;
        case 'contributors':
          return b.total_contributors - a.total_contributors;
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });
  };

  const filteredStudyPrograms = getFilteredStudyPrograms();
  const sortedStudyPrograms = getSortedStudyPrograms();

  const getTopThreeStudyPrograms = () => {
    return sortedStudyPrograms.slice(0, 3);
  };

  const getRestOfStudyProgramsList = () => {
    return sortedStudyPrograms.slice(3);
  };

  // Filter and sort user data
  useEffect(() => {
    if (activeTab === 'study_programs') return;

    let filtered = leaderboardData;

    // Filter by search text
    if (searchText) {
      filtered = filtered.filter(user =>
        user.name.toLowerCase().includes(searchText.toLowerCase()) ||
        user.nim_nip.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Sort data
    filtered = [...filtered].sort((a, b) => {
      if (sortBy === 'points') {
        return b.total_points - a.total_points;
      } else if (sortBy === 'videos') {
        return b.total_videos - a.total_videos;
      } else if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      }
      return 0;
    });

    setFilteredData(filtered);
  }, [searchText, sortBy, leaderboardData, activeTab]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSearchText('');
  };

  const getTopThree = () => {
    return filteredData.slice(0, 3);
  };

  const getRestOfList = () => {
    return filteredData.slice(3);
  };

  const getTotalStats = () => {
    if (activeTab === 'study_programs') {
      const totalPrograms = filteredStudyPrograms.length;
      const totalVideos = filteredStudyPrograms.reduce((sum, prodi) => sum + prodi.total_videos, 0);
      const totalPoints = filteredStudyPrograms.reduce((sum, prodi) => sum + prodi.total_points, 0);
      return { totalUsers: totalPrograms, totalVideos, totalPoints };
    } else {
      const totalUsers = filteredData.length;
      const totalVideos = filteredData.reduce((sum, user) => sum + user.total_videos, 0);
      const totalPoints = filteredData.reduce((sum, user) => sum + user.total_points, 0);
      return { totalUsers, totalVideos, totalPoints };
    }
  };

  const stats = getTotalStats();

  const getActiveTab = (activeTab) => {
    if (activeTab === 'students') return 'Mahasiswa';
    if (activeTab === 'lecturers') return 'Dosen';
    if (activeTab === 'teaching_staff') return 'Tenaga Pendidik';
    return 'Unit ITERA';
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    setUser(null);
    message.success('Logout berhasil');
  };

  const handleVideoSubmit = () => {
    if (!user) {
      message.warning('Silakan login terlebih dahulu untuk submit video');
      navigate('/login');
      return;
    }
    setVideoSubmissionModalVisible(true);
  };

  const handleVideoSubmissionSuccess = (_data) => {
    message.success('Video berhasil didaftarkan! Status: Menunggu Verifikasi');
    // Optionally reload data or show notification
  };

  // Easter egg handlers
  const handleTrophyClick = () => {
    setTrophySpinning(true);
    setClickCount(prev => {
      const newCount = prev + 1;
      if (newCount === 5) {
        message.success('🎉 Selamat! Anda menemukan easter egg!');
      } else if (newCount === 10) {
        message.success('🌟 Wow! 10 klik! Anda sangat antusias!');
      } else if (newCount % 20 === 0) {
        message.success(`🏆 ${newCount} klik! Champion!`);
      }
      return newCount;
    });

    setTimeout(() => setTrophySpinning(false), 600);
  };

  const handleYearBadgeClick = () => {
    setYearBadgeClicked(true);
    const messages = [
      '🎊 Selamat Tahun ' + new Date().getFullYear() + '!',
      '✨ Mari berkontribusi di tahun ini!',
      '🚀 Tahun yang produktif!',
      '💪 Semangat upload konten AI!',
      '🎯 Target 100 video tahun ini!'
    ];
    const randomMessage = messages[Math.floor(Math.random() * messages.length)];
    message.success(randomMessage);

    setTimeout(() => setYearBadgeClicked(false), 500);
  };

  const userMenu = {
    items: [
      {
        key: 'profile-header',
        icon: <UserOutlined />,
        label: user?.name || user?.email || 'Profile',
        disabled: true,
        style: {
          cursor: 'default',
          background: 'rgba(20, 184, 166, 0.05)',
          fontWeight: 600,
        },
      },
      {
        type: 'divider',
      },
      {
        key: 'my-videos',
        icon: <VideoCameraAddOutlined />,
        label: 'Video Saya',
        onClick: () => navigate('/my-videos'),
      },
      ...(user?.role === 'admin' || user?.is_admin ? [{
        key: 'admin',
        icon: <SettingOutlined />,
        label: 'Admin Panel',
        onClick: () => navigate('/admin'),
      }] : []),
      ...(user?.role === 'verifier' ? [{
        key: 'verificator',
        icon: <SettingOutlined />,
        label: 'Verificator Panel',
        onClick: () => navigate('/verificator'),
      }] : []),
      {
        type: 'divider',
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: 'Logout',
        onClick: handleLogout,
        danger: true,
      },
    ],
  };

  return (
    <div className="home-container">
      <div className="home-header">
        <div className="header-content">
          <TrophyOutlined
            className={`trophy-icon ${trophySpinning ? 'spin' : ''}`}
            onClick={handleTrophyClick}
          />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <h1>GAMIFIKASI #AI-ITERA</h1>
              <span
                className={`year-badge ${yearBadgeClicked ? 'clicked' : ''}`}
                onClick={handleYearBadgeClick}
              >
                {new Date().getFullYear()}
              </span>
            </div>
            <p className="subtitle">Perankingan Kontribusi Konten AI - Tahun {new Date().getFullYear()}</p>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            {user ? (
              <Dropdown menu={userMenu} placement="bottomRight" trigger={['click']}>
                <Space className="user-profile-trigger">
                  <Avatar
                    src={user?.picture}
                    icon={<UserOutlined />}
                    size={40}
                    style={{
                      background: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)',
                      boxShadow: '0 2px 8px rgba(20, 184, 166, 0.3)',
                      cursor: 'pointer',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (user.role === 'admin' || user.is_admin) {
                        navigate('/admin');
                      } else if (user.role === 'verifier') {
                        navigate('/verificator');
                      } else {
                        navigate('/my-videos');
                      }
                    }}
                  />
                  <div className="user-info">
                    <span className="user-name">
                      {user.name || user.email}
                    </span>
                    <span className="user-role">
                      {user.role === 'admin' || user.is_admin ? 'Admin' :
                       user.role === 'verifier' ? 'Verifier' : 'User'}
                    </span>
                  </div>
                </Space>
              </Dropdown>
            ) : (
              <Button
                type="primary"
                icon={<LoginOutlined />}
                onClick={() => navigate('/login')}
              >
                Login
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Upload Promotion Banner - More Prominent */}
      <div style={{ margin: '20px auto', maxWidth: '1400px', padding: '0 20px' }}>
        <UploadPromotionBanner />
      </div>

      {/* Certification Statistics Section */}
      {!summaryLoading && summaryData?.certifications && (
        <>
          <h2 className="certification-stats-title">
            <SafetyCertificateOutlined /> Statistik Sertifikasi
          </h2>
          <div className="stats-container">
            <div className="stat-card">
              <div className="stat-number">{summaryData.certifications.total_verified}</div>
              <div className="stat-label">Total Sertifikasi</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{summaryData.certifications.by_role.dosen}</div>
              <div className="stat-label">Sertifikasi Dosen</div>
              <div className="stat-sublabel">{summaryData.certifications.unique_holders.dosen} orang</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{summaryData.certifications.by_role.mahasiswa}</div>
              <div className="stat-label">Sertifikasi Mahasiswa</div>
              <div className="stat-sublabel">{summaryData.certifications.unique_holders.mahasiswa} orang</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{summaryData.certifications.by_role.tendik}</div>
              <div className="stat-label">Sertifikasi Tendik</div>
              <div className="stat-sublabel">{summaryData.certifications.unique_holders.tendik} orang</div>
            </div>
          </div>
          {summaryData.certifications.by_type && Object.keys(summaryData.certifications.by_type).length > 0 && (
            <div className="cert-type-breakdown">
              <div className="cert-type-inner">
                <h3 className="cert-type-title">Berdasarkan Jenis Sertifikasi</h3>
                <div className="cert-type-grid">
                  {Object.entries(summaryData.certifications.by_type).map(([type, count]) => (
                    <div key={type} className="cert-type-item">
                      <span className="cert-type-name">
                        {type === 'google-ai-essentials' ? 'Google AI Essentials' :
                         type === 'google-cloud-ai' ? 'Google Cloud AI' :
                         type === 'google-ml-crash-course' ? 'Google ML Crash Course' :
                         type === 'google-tensorflow' ? 'Google TensorFlow' :
                         'Lainnya'}
                      </span>
                      <span className="cert-type-count">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-number">{stats.totalUsers}</div>
          <div className="stat-label">Total {getActiveTab(activeTab)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.totalVideos}</div>
          <div className="stat-label">Total Video</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.totalPoints}</div>
          <div className="stat-label">Total Poin</div>
        </div>
      </div>

      <div className="leaderboard-section">
        <Tabs activeKey={activeTab} onChange={handleTabChange} className="leaderboard-tabs">

          <TabPane tab="Unit ITERA" key="study_programs">
            <div className="filter-section">
              <Input
                placeholder="Cari Unit ITERA"
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="search-input"
                allowClear
              />
              <Select
                value={selectedCampaign}
                onChange={setSelectedCampaign}
                className="sort-select"
                placeholder="Semua Campaign"
                allowClear
              >
                <Option value={null}>Semua Campaign</Option>
                {availableCampaigns.map(campaign => (
                  <Option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </Option>
                ))}
              </Select>
              <Select
                value={sortBy}
                onChange={setSortBy}
                className="sort-select"
              >
                <Option value="points">Urutkan: Poin Tertinggi</Option>
                <Option value="videos">Urutkan: Video Terbanyak</Option>
                <Option value="contributors">Urutkan: Kontributor Terbanyak</Option>
                <Option value="name">Urutkan: Nama A-Z</Option>
              </Select>
            </div>

            {loading ? (
              <div className="loading-container">
                <Spin size="large" tip="Memuat data unit ITERA..." />
              </div>
            ) : filteredStudyPrograms.length === 0 ? (
              <Empty
                description="Tidak ada unit ITERA yang ditemukan"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <>
                {getTopThreeStudyPrograms().length > 0 && (
                  <div className="podium-section">
                    <h2 className="section-title">
                      🏆 Top 3 Unit ITERA Berdasarkan Kontribusi
                    </h2>
                    <div className="podium-container">
                      {getTopThreeStudyPrograms().map((studyProgram, index) => (
                        <StudyProgramLeaderboardCard
                          key={studyProgram.code}
                          rank={index + 1}
                          studyProgram={studyProgram}
                          isTopThree={true}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {getRestOfStudyProgramsList().length > 0 && (
                  <div className="list-section">
                    <h2 className="section-title">Daftar Lengkap Unit ITERA</h2>
                    <div className="list-container">
                      {getRestOfStudyProgramsList().map((studyProgram, index) => (
                        <StudyProgramLeaderboardCard
                          key={studyProgram.code}
                          rank={index + 4}
                          studyProgram={studyProgram}
                          isTopThree={false}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </TabPane>

          <TabPane tab="Dosen" key="lecturers">
            <div className="filter-section">
              <Input
                placeholder="Cari nama atau NIP..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="search-input"
                allowClear
              />
              <Select
                value={selectedCampaign}
                onChange={setSelectedCampaign}
                className="sort-select"
                placeholder="Semua Campaign"
                allowClear
              >
                <Option value={null}>Semua Campaign</Option>
                {availableCampaigns.map(campaign => (
                  <Option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </Option>
                ))}
              </Select>
              <Select
                value={sortBy}
                onChange={setSortBy}
                className="sort-select"
              >
                <Option value="points">Urutkan: Poin Tertinggi</Option>
                <Option value="videos">Urutkan: Video Terbanyak</Option>
                <Option value="name">Urutkan: Nama A-Z</Option>
              </Select>
            </div>

            {loading ? (
              <div className="loading-container">
                <Spin size="large" tip="Memuat data..." />
              </div>
            ) : filteredData.length === 0 ? (
              <Empty
                description="Tidak ada data yang ditemukan"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <>
                {getTopThree().length > 0 && (
                  <div className="podium-section">
                    <h2 className="section-title">🏆 Top 3 Dosen</h2>
                    <div className="podium-container">
                      {getTopThree().map((user, index) => (
                        <LeaderboardCard
                          key={user.nim_nip}
                          rank={index + 1}
                          user={user}
                          isTopThree={true}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {getRestOfList().length > 0 && (
                  <div className="list-section">
                    <h2 className="section-title">Daftar Lengkap</h2>
                    <div className="list-container">
                      {getRestOfList().map((user, index) => (
                        <LeaderboardCard
                          key={user.nim_nip}
                          rank={index + 4}
                          user={user}
                          isTopThree={false}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </TabPane>

          <TabPane tab="Tendik" key="teaching_staff">
            <div className="filter-section">
              <Input
                placeholder="Cari nama atau NIP..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="search-input"
                allowClear
              />
              <Select
                value={selectedCampaign}
                onChange={setSelectedCampaign}
                className="sort-select"
                placeholder="Semua Campaign"
                allowClear
              >
                <Option value={null}>Semua Campaign</Option>
                {availableCampaigns.map(campaign => (
                  <Option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </Option>
                ))}
              </Select>
              <Select
                value={sortBy}
                onChange={setSortBy}
                className="sort-select"
              >
                <Option value="points">Urutkan: Poin Tertinggi</Option>
                <Option value="videos">Urutkan: Video Terbanyak</Option>
                <Option value="name">Urutkan: Nama A-Z</Option>
              </Select>
            </div>

            {loading ? (
              <div className="loading-container">
                <Spin size="large" tip="Memuat data..." />
              </div>
            ) : filteredData.length === 0 ? (
              <Empty
                description="Tidak ada data yang ditemukan"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <>
                {getTopThree().length > 0 && (
                  <div className="podium-section">
                    <h2 className="section-title">🏆 Top 3 Tenaga Pendidik</h2>
                    <div className="podium-container">
                      {getTopThree().map((user, index) => (
                        <LeaderboardCard
                          key={user.nim_nip}
                          rank={index + 1}
                          user={user}
                          isTopThree={true}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {getRestOfList().length > 0 && (
                  <div className="list-section">
                    <h2 className="section-title">Daftar Lengkap</h2>
                    <div className="list-container">
                      {getRestOfList().map((user, index) => (
                        <LeaderboardCard
                          key={user.nim_nip}
                          rank={index + 4}
                          user={user}
                          isTopThree={false}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </TabPane>

          <TabPane tab="Mahasiswa" key="students">
            <div className="filter-section">
              <Input
                placeholder="Cari nama atau NIM..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="search-input"
                allowClear
              />
              <Select
                value={selectedCampaign}
                onChange={setSelectedCampaign}
                className="sort-select"
                placeholder="Semua Campaign"
                allowClear
              >
                <Option value={null}>Semua Campaign</Option>
                {availableCampaigns.map(campaign => (
                  <Option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </Option>
                ))}
              </Select>
              <Select
                value={sortBy}
                onChange={setSortBy}
                className="sort-select"
              >
                <Option value="points">Urutkan: Poin Tertinggi</Option>
                <Option value="videos">Urutkan: Video Terbanyak</Option>
                <Option value="name">Urutkan: Nama A-Z</Option>
              </Select>
            </div>

            {loading ? (
              <div className="loading-container">
                <Spin size="large" tip="Memuat data..." />
              </div>
            ) : filteredData.length === 0 ? (
              <Empty
                description="Tidak ada data yang ditemukan"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <>
                {getTopThree().length > 0 && (
                  <div className="podium-section">
                    <h2 className="section-title">🏆 Top 3 Mahasiswa</h2>
                    <div className="podium-container">
                      {getTopThree().map((user, index) => (
                        <LeaderboardCard
                          key={user.nim_nip}
                          rank={index + 1}
                          user={user}
                          isTopThree={true}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {getRestOfList().length > 0 && (
                  <div className="list-section">
                    <h2 className="section-title">Daftar Lengkap</h2>
                    <div className="list-container">
                      {getRestOfList().map((user, index) => (
                        <LeaderboardCard
                          key={user.nim_nip}
                          rank={index + 4}
                          user={user}
                          isTopThree={false}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </TabPane>
        </Tabs>
      </div>

      {/* Floating Action Button for Video Submission */}
      <Tooltip title={user ? "Submit Video Saya" : "Login untuk Submit Video"} placement="left">
        <FloatButton
          icon={<VideoCameraAddOutlined />}
          type="primary"
          style={{
            right: 24,
            bottom: 24,
            width: 60,
            height: 60,
          }}
          onClick={handleVideoSubmit}
        />
      </Tooltip>

      {/* Video Submission Modal */}
      <VideoSubmissionModal
        visible={videoSubmissionModalVisible}
        onClose={() => setVideoSubmissionModalVisible(false)}
        onSuccess={handleVideoSubmissionSuccess}
      />
    </div>
  );
};

export default Home;
